import styled from "@emotion/styled";
import { LocalDate, LocalDateTime } from "@js-joda/core";
import React from "react";
import { VisitInstanceId } from "../messages";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { PatientScheduleVisitCard } from "./PatientScheduleVisitCard";
import ScaffoldTitle from "./Scaffold/components/ScaffoldTitle";
import Scaffold from "./Scaffold/Scaffold";

type Props = {
  visits: {
    id: VisitInstanceId;
    startDateTime: LocalDateTime;
    endDateTime: LocalDateTime;
    caregiverName: string;
    caregiverPhoneNumber: string | null;
  }[];
  onClickGoBack: () => void;
};

export default function PatientScheduleScreen(props: Props) {
  const today = LocalDate.now();

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.PATIENT_SCHEDULE)} />}
      onClickGoBack={props.onClickGoBack}
    >
      {props.visits.length === 0 && (
        <EmptyState>
          <div>
            <h2>{translate(STRINGS.NO_VISITS_SCHEDULED)}</h2>
            <p>{translate(STRINGS.NO_VISITS_SCHEDULED_MESSAGE)}</p>
          </div>
        </EmptyState>
      )}
      <div className="CaregiverScheduleScreen_DaysWithVisits">
        {props.visits.map((visit, index) => {
          const showMonthIndicator =
            index === 0 ||
            !visit.startDateTime.month().equals(props.visits[index - 1].startDateTime.month());

          return (
            <React.Fragment key={visit.id.toString()}>
              {showMonthIndicator && (
                <MonthIndicator>
                  <span>{visit.startDateTime.month().toString()}</span>
                </MonthIndicator>
              )}
              <PatientScheduleVisitCard
                index={index}
                startDateTime={visit.startDateTime}
                endDateTime={visit.endDateTime}
                caregiverName={visit.caregiverName}
                caregiverPhoneNumber={visit.caregiverPhoneNumber}
                isCurrentDay={visit.startDateTime.toLocalDate().equals(today)}
              />
            </React.Fragment>
          );
        })}
      </div>
    </Scaffold>
  );
}

const MonthIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 16px;
  padding: 0 16px;
  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid var(--gray-200);
  }
  &::before {
    margin-right: 8px;
  }
  &::after {
    margin-left: 8px;
  }
  span {
    font-size: 14px;
    font-weight: bold;
    color: var(--gray-500);
  }
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  > div {
    padding: 32px;
    text-align: center;
  }

  h2 {
    font-size: 18px;
    margin: 0;
    color: var(--gray-500);
  }

  p {
    font-size: 16px;
    margin: 0;
    color: var(--gray-400);
  }
`;
